import React from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ProjectPage from "@/pages/Project";
import SettingsPage from "@/pages/Settings";
import AdminPage from "@/pages/Admin";
import DatabaseAdminPage from "@/pages/DatabaseAdmin";
import StorageAdminPage from "@/pages/StorageAdmin";
import ErrorLogsAdmin from "@/pages/ErrorLogsAdmin";
import ResetPassword from "@/pages/ResetPassword";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="overline text-[--muted]" data-testid="auth-loading">Checking session…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="overline text-[--muted]">Checking session…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function InnerRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Public registration is disabled — admin-created accounts only. */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:id"
        element={
          <ProtectedRoute>
            <ProjectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <AdminRoute>
            <SettingsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/database"
        element={
          <AdminRoute>
            <DatabaseAdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/storage"
        element={
          <AdminRoute>
            <StorageAdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/errors"
        element={
          <AdminRoute>
            <ErrorLogsAdmin />
          </AdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <InnerRouter />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
